import { useApi } from '@/api';
import { ProgressBar, InfoIcon } from '@/components';
import type { ProgressBarProps } from '@/components/ProgressBar';
import sizes from '@/theme/sizes';
import {
  Card,
  CurrencyValue,
  DoughnutChart,
  Flex,
  formatPercentage,
  Heading,
  hexToRgb,
  TableSkeleton,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useIsMobile
} from '@blockanalitica/ui';
import { useMemo, ReactNode, useCallback } from 'react';
import { determineAssetColor } from '@/utils/colors';

type TreasuryAssetData = {
  name: string;
  value: string;
  category: string;
  sub_category: string;
  percent_of_total: string;
  percent_of_category: string;
  percent_of_circulating_locked: string;
};

interface ProgressData extends ProgressBarProps {
  text: string | ReactNode;
  name: string;
  percent: number;
  percentOfCategory: number;
}
export default function SkySupplyDistribution() {
  const isMobile = useIsMobile();
  const [data, error] = useApi<TreasuryAssetData[]>({
    url: '/sky-supply/distribution/'
  });

  const processedData: ProgressData[] | null = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }
    // Sort data by percentage in descending order
    const sortedData = [...data].sort(
      (a, b) => Number(b.percent_of_total) - Number(a.percent_of_total)
    );

    // Split into top 5 and others and transform them
    const topFive = sortedData.slice(0, 4).map((item) => {
      // Determine display name based on category and sub_category
      const formattedSubCategory = item.sub_category.replace(/-/g, ' ');
      const formattedCategory = item.category.replace(/-/g, ' ');

      const categoryDisplay =
        item.category === item.sub_category
          ? formattedCategory
          : `${formattedCategory}-${formattedSubCategory}`;

      return {
        text: (
          <Flex gap="xs" flexDirection="column">
            <span>
              {item.name} ({categoryDisplay})
            </span>
            <Flex gap="2xs" fontSize="-1" color="muted">
              <span>
                ({formatPercentage(Number(item.percent_of_total))} of total)
              </span>
              <span>
                ({formatPercentage(Number(item.percent_of_category))} of{' '}
                {item.category})
              </span>
            </Flex>
          </Flex>
        ),
        name: item.name,
        percent: Number(item.percent_of_total),
        percentOfCategory: Number(item.percent_of_category),
        additionalValue: <CurrencyValue value={item.value} />,
        color: determineAssetColor(item.sub_category)
      };
    });

    const others = sortedData.slice(4);
    // Calculate total percentage/amount for others
    const othersPercentage = others.reduce(
      (sum, item) => sum + Number(item.percent_of_total),
      0
    );
    const othersAmount = others.reduce(
      (sum, item) => sum + Number(item.value),
      0
    );
    const othersCategoryPercentage = others.reduce(
      (sum, item) => sum + Number(item.percent_of_category),
      0
    );

    // Return top 5 plus Others if there are more than 5 items
    if (others.length > 0) {
      return [
        ...topFive,
        {
          text: (
            <Flex gap="xs" flexDirection="column">
              <span>Others</span>
              <Flex gap="2xs" fontSize="-1" color="muted">
                <span>({formatPercentage(othersPercentage)} of total)</span>
              </Flex>
            </Flex>
          ),
          name: 'Others',
          percent: othersPercentage,
          percentOfCategory: othersCategoryPercentage,
          additionalValue: <CurrencyValue value={othersAmount} />,
          color: determineAssetColor('Others'),
          breakdown: others.map((item) => {
            const formattedSubCategory = item.sub_category.replace(/-/g, ' ');
            const formattedCategory = item.category.replace(/-/g, ' ');

            const categoryDisplay =
              item.category === item.sub_category
                ? formattedCategory
                : `${formattedCategory}-${formattedSubCategory}`;

            return {
              text: (
                <Flex gap="xs" flexDirection="column">
                  <span>
                    {item.name}({categoryDisplay})
                  </span>
                  <Flex gap="2xs" fontSize="-1" color="muted">
                    <span>
                      ({formatPercentage(Number(item.percent_of_total))} of
                      total)
                    </span>
                    <span>
                      ({formatPercentage(Number(item.percent_of_category))} of{' '}
                      {item.category})
                    </span>
                  </Flex>
                </Flex>
              ),
              percent: item.percent_of_total,
              additionalValue: <CurrencyValue value={item.value} />
            };
          })
        }
      ];
    }

    return topFive;
  }, [data]);

  const customTooltip = useCallback(
    (context: { chart: any; tooltip: any; replay: boolean }) => {
      if (!processedData || !data) {
        return null;
      }

      const { chart, tooltip } = context;

      // Get or create tooltip element
      let tooltipEl = chart.canvas?.parentNode?.querySelector('div');
      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
        tooltipEl.style.borderRadius = sizes['-2'];
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.transform = 'translate(-50%, 0)';
        tooltipEl.style.transition = 'all .1s ease';
        chart.canvas?.parentNode?.appendChild(tooltipEl);
      }

      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = '0';
        return;
      }

      const dataIndex = tooltip.dataPoints[0].dataIndex;
      const item = processedData[dataIndex];

      // Find the corresponding original data item
      const originalItem = data.find((d) => d.name === item.name);
      const formattedSubCategory = originalItem?.sub_category.replace(
        /-/g,
        ' '
      );
      const formattedCategory = originalItem?.category.replace(/-/g, ' ');

      const categoryDisplay =
        originalItem?.category === originalItem?.sub_category
          ? formattedCategory
          : `${formattedCategory}-${formattedSubCategory}`;

      const content = `
        <div style="
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 8px;
          color: white;
          font-size: 11px;
        ">
          <div style="
            display: flex;
            align-items: center;
            gap: 6px;
            font-weight: bold;
          ">
            <span style="
              width: 12px;
              height: 12px;
              border-radius: 50%;
              background-color: ${tooltip.labelColors[0].backgroundColor};
              border: 2px solid ${tooltip.labelColors[0].borderColor};
              display: inline-block;
            "></span>
            ${item.name} (${categoryDisplay})
          </div>
          <div>% of total: ${formatPercentage(item.percent)}</div>
          <div>% of ${formattedCategory}: ${formatPercentage(item.percentOfCategory)}</div>
          <div>Amount: ${
            originalItem?.value
              ? new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  notation: 'compact',
                  maximumFractionDigits: 2
                }).format(Number(originalItem.value))
              : ''
          }</div>
        </div>
      `;

      tooltipEl.innerHTML = content;
      const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
      tooltipEl.style.opacity = '1';
      tooltipEl.style.left = positionX + tooltip.caretX + 'px';
      tooltipEl.style.top = positionY + tooltip.caretY + 'px';
    },
    [processedData, data]
  );
  const customConfig = useMemo(
    () => ({
      options: {
        layout: {
          padding: 10
        },
        plugins: {
          legend: {},
          tooltip: {
            enabled: false,
            position: 'nearest' as const,
            external: (context) => customTooltip(context)
          }
        }
      }
    }),
    [customTooltip]
  );

  const chartData = useMemo(() => {
    if (!processedData) {
      return;
    }

    const labels: string[] = [];
    const backgroundColor: string[] = [];
    const colorsBorder: string[] = [];
    const datasetData: number[] = [];

    processedData.forEach((row) => {
      const { r, g, b } = hexToRgb(row.color);
      labels.push(row.name);
      backgroundColor.push(`rgba(${r}, ${g}, ${b}, 0.5)`);
      colorsBorder.push(`rgba(${r}, ${g}, ${b}, 1)`);
      datasetData.push(row.percent);
    });

    return {
      labels,
      backgroundColor,
      colorsBorder,
      datasets: [
        {
          data: datasetData,
          hoverOffset: 15
        }
      ]
    };
  }, [processedData]);

  if (error) {
    throw error;
  }
  if (!data) {
    // TODO: wrong skeleton
    return <TableSkeleton rows={10} columns={6} />;
  }

  if (!processedData || !chartData) {
    return null;
  }

  return (
    <Card flexDirection="column" gap="s">
      <Flex gap="3xs" alignItems="center">
        <Heading size="4">Sky Supply Breakdown</Heading>
        <Tooltip>
          <TooltipTrigger>
            <InfoIcon size="0" />
          </TooltipTrigger>
          <TooltipContent>
            <Text>
              <strong>Non-Circulating Supply:</strong>
              <br />
              <br />
              SKY tokens held by the treasury and not available for public
              circulation.
              <br />
              <br />
              <strong>Circulating Supply:</strong>
              <br />
              <br />
              Total SKY supply minus non-circulating tokens available to the
              public.
              <br />
              <br />
              <strong>Locked Supply:</strong>
              <br />
              <br />
              Circulating SKY tokens locked in smart contracts and temporarily
              unavailable for trading.
              <br />
              <br />
              <strong>Not-Migrated Supply:</strong>
              <br />
              <br />
              SKY tokens that have not been migrated from MKR to SKY, and are
              still circulating as MKR.
            </Text>
          </TooltipContent>
        </Tooltip>
      </Flex>
      <Flex gap="s">
        <Flex flexDirection="column" gap="m" flex="1">
          {processedData.map((row) => (
            <ProgressBar key={row.name} {...row} />
          ))}
        </Flex>

        {!isMobile ? (
          <Card flexDirection="column" gap="m" backgroundColor="transparent">
            {/* 
                DoughnutChart needs to be wrapped in Flex (technically div) in order
                for the custom tooltip to attach to the correct parent
              */}
            <Flex>
              <DoughnutChart
                config={customConfig}
                data={chartData}
                valueFormatter={formatPercentage}
              />
            </Flex>
          </Card>
        ) : null}
      </Flex>
    </Card>
  );
}
